import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { GlobalsService } from '../../services/globals.service';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';
import { AccountDetailModalComponent, AccountDetailModalData } from '../account-detail-modal/account-detail-modal.component';
import { POSTED_FILTER_OPTIONS, PostedFilterMode } from '../../constants/posted-filter-options';

interface TagBreakdownRow {
	tagref: number | null;
	tagdescription: string;
	balance_at_date: number;
}

interface TrialBalanceRow {
	accountcode: string;
	accountname: string;
	group: string;
	balance_at_date: number;
	tag_breakdown?: TagBreakdownRow[];
}

interface GroupedRows {
	group: string;
	rows: TrialBalanceRow[];
	/** Sum of debit-column amounts for accounts in this group (positive signed balances). */
	subtotalDebit: number;
	/** Sum of credit-column amounts (absolute value of negative signed balances). */
	subtotalCredit: number;
}

@Component({
	selector: 'app-simple-trial-balance',
	templateUrl: './simple-trial-balance.component.html',
	styleUrls: ['./simple-trial-balance.component.scss']
})
export class SimpleTrialBalanceComponent implements OnInit {
	periods: any[] = [];
	formGroup: UntypedFormGroup;
	loading = false;
	exporting = false;
	trialBalanceList: TrialBalanceRow[] = [];
	groupedRows: GroupedRows[] = [];
	debitGrandTotal = 0;
	creditGrandTotal = 0;
	color: string;
	hasRun = false;
	selectedPeriodLabel = '';
	sortCol: 'accountcode' | 'accountname' | 'balance_at_date' = 'accountcode';
	sortDir: 'asc' | 'desc' = 'asc';
	hideZero = true;
	postedOptions = POSTED_FILTER_OPTIONS;

	constructor(
		private location: Location,
		private dialog: MatDialog,
		private globalsService: GlobalsService,
		private generalLedgerService: GeneralLedgerService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private fb: UntypedFormBuilder
	) {
		this.color = this.globalSearchService.getColor();
		this.formGroup = this.fb.group({
			period: [null, Validators.required],
			posted: ['all', Validators.required]
		});
	}

	ngOnInit(): void {
		this.generalLedgerService.getPeriods().subscribe((results: any) => {
			this.periods = results.periods || [];
			const defaultTo = results.default_toperiod;
			if (defaultTo != null && this.periods.length) {
				this.formGroup.patchValue({ period: defaultTo });
				this.updatePeriodLabel(defaultTo);
			}
		});
	}

	back(): void {
		this.location.back();
	}

	onPeriodChange(): void {
		const val = this.formGroup.get('period')?.value;
		this.updatePeriodLabel(val);
		this.trialBalanceList = [];
		this.groupedRows = [];
		this.hasRun = false;
	}

	updatePeriodLabel(periodno: any): void {
		const match = this.periods.find(p => p.periodno == periodno);
		this.selectedPeriodLabel = match ? match.lastdate_in_period : '';
	}

	/** Coerce API balances to finite numbers so grand totals never string-concatenate. */
	private toBalanceNum(v: unknown): number {
		const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/,/g, ''));
		return Number.isFinite(n) ? n : 0;
	}

	loadTrialBalance(): void {
		const period = this.formGroup.get('period')?.value;
		if (period == null) return;
		this.loading = true;
		const posted = (this.formGroup.get('posted')?.value as PostedFilterMode) || 'all';
		this.generalLedgerService.getSimpleTrialBalance(Number(period), posted).subscribe({
			next: (data: any) => {
				const raw = Array.isArray(data) ? data : [];
				this.trialBalanceList = raw.map((r: TrialBalanceRow) => ({
					...r,
					balance_at_date: this.toBalanceNum(r.balance_at_date),
					tag_breakdown: Array.isArray(r.tag_breakdown)
						? r.tag_breakdown.map((t: TagBreakdownRow) => ({
								...t,
								balance_at_date: this.toBalanceNum(t.balance_at_date)
							}))
						: r.tag_breakdown
				}));
				this.buildGroups();
				this.loading = false;
				this.hasRun = true;
			},
			error: () => {
				this.trialBalanceList = [];
				this.groupedRows = [];
				this.debitGrandTotal = 0;
				this.creditGrandTotal = 0;
				this.loading = false;
				this.hasRun = true;
			}
		});
	}

	buildGroups(): void {
		const map = new Map<string, TrialBalanceRow[]>();
		let dGrand = 0;
		let cGrand = 0;
		for (const row of this.trialBalanceList) {
			const bal = this.toBalanceNum(row.balance_at_date);
			if (this.hideZero && Math.abs(bal) < 0.0000001) continue;
			if (bal > 0) {
				dGrand += bal;
			} else if (bal < 0) {
				cGrand += -bal;
			}
			if (!map.has(row.group)) map.set(row.group, []);
			map.get(row.group)!.push(row);
		}
		this.debitGrandTotal = dGrand;
		this.creditGrandTotal = cGrand;
		this.groupedRows = [];
		map.forEach((rows, group) => {
			let subtotalDebit = 0;
			let subtotalCredit = 0;
			for (const r of rows) {
				const b = this.toBalanceNum(r.balance_at_date);
				if (this.hideZero && Math.abs(b) < 0.0000001) continue;
				if (b > 0) subtotalDebit += b;
				else if (b < 0) subtotalCredit += -b;
			}
			this.groupedRows.push({ group, rows, subtotalDebit, subtotalCredit });
		});
		this.groupedRows.sort((a, b) => a.group.localeCompare(b.group));
		this.applySortToGroups();
	}

	toggleHideZero(): void {
		this.hideZero = !this.hideZero;
		this.buildGroups();
	}

	allRowsHiddenByZeroFilter(): boolean {
		return this.hideZero && this.trialBalanceList.length > 0 && this.groupedRows.length === 0;
	}

	sort(col: 'accountcode' | 'accountname' | 'balance_at_date'): void {
		if (this.sortCol === col) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortCol = col;
			this.sortDir = 'asc';
		}
		this.applySortToGroups();
	}

	applySortToGroups(): void {
		const col = this.sortCol;
		const dir = this.sortDir === 'asc' ? 1 : -1;
		for (const grp of this.groupedRows) {
			grp.rows.sort((a, b) => {
				const av = a[col];
				const bv = b[col];
				if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
				return String(av).localeCompare(String(bv)) * dir;
			});
		}
	}

	debitAmount(n: number): number | null {
		return n > 0 ? n : null;
	}

	creditAmount(n: number): number | null {
		return n < 0 ? -n : null;
	}

	formatMoney(n: number | null): string {
		if (n == null) {
			return '';
		}
		return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
	}

	tbBalanced(): boolean {
		return Math.abs(this.debitGrandTotal - this.creditGrandTotal) < 0.005;
	}

	visibleTagBreakdown(row: TrialBalanceRow): TagBreakdownRow[] {
		const b = row.tag_breakdown;
		if (!b?.length) {
			return [];
		}
		if (!this.hideZero) {
			return b;
		}
		return b.filter(t => Math.abs(this.toBalanceNum(t.balance_at_date)) >= 0.0000001);
	}

	openAccount(row: TrialBalanceRow): void {
		const period = this.formGroup.get('period')?.value;
		const modalData: AccountDetailModalData = {
			accountcode: row.accountcode,
			accountname: row.accountname,
			periodno: period,
			periodLabel: this.selectedPeriodLabel,
			posted: ((this.formGroup.get('posted')?.value as PostedFilterMode) || 'all')
		};
		const ref = this.dialog.open(AccountDetailModalComponent, {
			width: '1000px',
			maxWidth: '96vw',
			maxHeight: '90vh',
			data: modalData
		});
		ref.beforeClosed().subscribe(() => {
			if (ref.componentInstance?.parentShouldRefreshLists) {
				this.loadTrialBalance();
			}
		});
	}

	exportExcel(): void {
		const data: {
			'Account Code': string;
			'Account Name': string;
			'Group': string;
			Tag: string;
			Debit: number | '';
			Credit: number | '';
		}[] = [];
		for (const r of this.trialBalanceList) {
			const bal = this.toBalanceNum(r.balance_at_date);
			if (this.hideZero && Math.abs(bal) < 0.0000001) {
				continue;
			}
			const tags = this.visibleTagBreakdown(r);
			if (tags.length) {
				for (const t of tags) {
					const td = this.debitAmount(this.toBalanceNum(t.balance_at_date));
					const tc = this.creditAmount(this.toBalanceNum(t.balance_at_date));
					data.push({
						'Account Code': r.accountcode,
						'Account Name': r.accountname,
						'Group': r.group,
						Tag: t.tagdescription,
						Debit: td ?? '',
						Credit: tc ?? ''
					});
				}
				const rd = this.debitAmount(bal);
				const rc = this.creditAmount(bal);
				data.push({
					'Account Code': r.accountcode,
					'Account Name': r.accountname,
					'Group': r.group,
					Tag: 'Account total',
					Debit: rd ?? '',
					Credit: rc ?? ''
				});
			} else {
				const rd = this.debitAmount(bal);
				const rc = this.creditAmount(bal);
				data.push({
					'Account Code': r.accountcode,
					'Account Name': r.accountname,
					'Group': r.group,
					Tag: '',
					Debit: rd ?? '',
					Credit: rc ?? ''
				});
			}
		}
		const period = this.formGroup.get('period')?.value ?? '';
		this.globalSearchService.exportJsonToExcel(data, `SimpleTrialBalance_P${period}_${new Date().toISOString().slice(0, 10)}.xlsx`);
	}

	/**
	 * PDF is rendered server-side (wkhtmltopdf) with only report header CSS.
	 * innerHTML would omit encapsulated styles and mat-icon ligatures show as raw text.
	 */
	private escPdf(v: unknown): string {
		return String(v ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	private formatPdfBalance(n: number): string {
		return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
	}

	private buildPdfTableHtml(): string {
		const esc = (v: unknown) => this.escPdf(v);
		const debitTd = (n: number) => {
			const d = n > 0 ? this.formatPdfBalance(n) : '';
			return `<td class="stb-pdf-debit" style="text-align:right;font-family:monospace,monospace;">${esc(d)}</td>`;
		};
		const creditTd = (n: number) => {
			const c = n < 0 ? this.formatPdfBalance(-n) : '';
			return `<td class="stb-pdf-credit" style="text-align:right;font-family:monospace,monospace;">${esc(c)}</td>`;
		};
		const debitTotalTd = (n: number) => {
			const d = n > 0.000001 ? this.formatPdfBalance(n) : '';
			return `<td class="stb-pdf-debit" style="text-align:right;font-family:monospace,monospace;font-weight:700;">${esc(d)}</td>`;
		};
		const creditTotalTd = (n: number) => {
			const c = n > 0.000001 ? this.formatPdfBalance(n) : '';
			return `<td class="stb-pdf-credit" style="text-align:right;font-family:monospace,monospace;font-weight:700;">${esc(c)}</td>`;
		};

		const style = `<style type="text/css">
			table.stb-pdf-table { width:100%; border-collapse:collapse; font-size:11px; color:#000; margin-top:8px; }
			table.stb-pdf-table th, table.stb-pdf-table td { padding:6px 8px; border:1px solid #ccc; vertical-align:top; }
			table.stb-pdf-table thead th { background:#e9ecef; font-weight:bold; text-align:left; }
			table.stb-pdf-table thead th.stb-pdf-debith,
			table.stb-pdf-table thead th.stb-pdf-credith { text-align:right; }
			table.stb-pdf-table tr.stb-pdf-grp td { background:#f1f3f5; font-weight:bold; }
			table.stb-pdf-table tr.stb-pdf-tag td { background:#fafbfc; font-size:10px; }
			table.stb-pdf-table tr.stb-pdf-sub td { background:#eef1f4; font-weight:600; }
			table.stb-pdf-table tr.stb-pdf-grand td { background:#e2e6ea; font-weight:700; }
			table.stb-pdf-table th.stb-pdf-code, table.stb-pdf-table td.stb-pdf-code { font-family:monospace,monospace; width:10%; }
			table.stb-pdf-table th.stb-pdf-name, table.stb-pdf-table td.stb-pdf-name { width:38%; }
			table.stb-pdf-table th.stb-pdf-tagc, table.stb-pdf-table td.stb-pdf-tagc { width:20%; }
			table.stb-pdf-table th.stb-pdf-debith, table.stb-pdf-table td.stb-pdf-debit { width:16%; }
			table.stb-pdf-table th.stb-pdf-credith, table.stb-pdf-table td.stb-pdf-credit { width:16%; }
		</style>`;

		let rows = '';
		for (const grp of this.groupedRows) {
			rows += `<tr class="stb-pdf-grp"><td colspan="5">${esc(grp.group)}</td></tr>`;
			for (const row of grp.rows) {
				rows += `<tr>
					<td class="stb-pdf-code">${esc(row.accountcode)}</td>
					<td class="stb-pdf-name">${esc(row.accountname)}</td>
					<td class="stb-pdf-tagc"></td>
					${debitTd(row.balance_at_date)}${creditTd(row.balance_at_date)}
				</tr>`;
				for (const tb of this.visibleTagBreakdown(row)) {
					rows += `<tr class="stb-pdf-tag">
						<td class="stb-pdf-code"></td>
						<td class="stb-pdf-name"></td>
						<td class="stb-pdf-tagc">${esc(tb.tagdescription)}</td>
						${debitTd(tb.balance_at_date)}${creditTd(tb.balance_at_date)}
					</tr>`;
				}
			}
			rows += `<tr class="stb-pdf-sub">
				<td class="stb-pdf-code"></td>
				<td class="stb-pdf-name" style="text-align:right;font-weight:600;">${esc(grp.group)} Total</td>
				<td class="stb-pdf-tagc"></td>
				${debitTotalTd(grp.subtotalDebit)}${creditTotalTd(grp.subtotalCredit)}
			</tr>`;
		}

		const foot = `<tfoot><tr class="stb-pdf-grand">
			<td class="stb-pdf-code"></td>
			<td class="stb-pdf-name" style="text-align:right;font-weight:700;">Grand Total</td>
			<td class="stb-pdf-tagc"></td>
			${debitTotalTd(this.debitGrandTotal)}${creditTotalTd(this.creditGrandTotal)}
		</tr></tfoot>`;

		const head = `<thead><tr>
			<th class="stb-pdf-code">Account</th>
			<th class="stb-pdf-name">Name</th>
			<th class="stb-pdf-tagc">Tag</th>
			<th class="stb-pdf-debith">Debit</th>
			<th class="stb-pdf-credith">Credit</th>
		</tr></thead>`;

		return `${style}<table class="table table-bordered stb-pdf-table">${head}<tbody>${rows}</tbody>${foot}</table>`;
	}

	exportPdf(): void {
		if (!this.trialBalanceList.length) return;
		this.exporting = true;
		const period = this.formGroup.get('period')?.value ?? '';
		const encoded: string = this.globalSearchService.base64encode(this.buildPdfTableHtml());
		const data = {
			content: encoded,
			filename: `SimpleTrialBalance_P${period}_${new Date().toISOString().slice(0, 10)}`,
			title: `Simple Trial Balance — Period ending: ${this.selectedPeriodLabel}`,
			landscape: true
		};
		this.printService.pdf(data).subscribe(
			(result: any) => {
				this.exporting = false;
				if (result && result.content) {
					this.globalSearchService.downloadPdf(result.content, data.filename);
				}
			},
			() => { this.exporting = false; }
		);
	}
}
